import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const TeacherResults = () => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [classRes, subjRes] = await Promise.all([
        api.get('/teacher/classes', { headers }),
        api.get('/teacher/subjects', { headers }),
      ]);

      const classesList = Array.isArray(classRes.data) ? classRes.data : [];
      setClasses(classesList);
      setSubjects(Array.isArray(subjRes.data) ? subjRes.data : []);

      if (classesList.length > 0) {
        setSelectedClass(classesList[0].id);
        fetchResults(classesList[0].id);
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (classId) => {
    try {
      setLoadingResults(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/teacher/classes/${classId}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
      setResults([]);
    } finally {
      setLoadingResults(false);
    }
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    if (classId) fetchResults(classId);
    else setResults([]);
  };

  const getAverageColor = (avg) => {
    if (avg >= 16) return 'text-green-600';
    if (avg >= 14) return 'text-blue-600';
    if (avg >= 12) return 'text-yellow-600';
    if (avg >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAppreciationColor = (app) => {
    if (app === 'Très bien') return 'bg-green-100 text-green-700';
    if (app === 'Bien') return 'bg-blue-100 text-blue-700';
    if (app === 'Assez bien') return 'bg-yellow-100 text-yellow-700';
    return 'bg-orange-100 text-orange-700';
  };

  // Stats
  const stats = useMemo(() => {
    if (results.length === 0) return { average: 0, best: null, worst: null, pass: 0, fail: 0 };
    const withAvg = results.filter((r) => r.average > 0);
    if (withAvg.length === 0) return { average: 0, best: null, worst: null, pass: 0, fail: 0 };
    const sum = withAvg.reduce((acc, r) => acc + r.average, 0);
    const average = (sum / withAvg.length).toFixed(2);
    const sorted = [...withAvg].sort((a, b) => b.average - a.average);
    const pass = withAvg.filter((r) => r.average >= 10).length;
    const fail = withAvg.filter((r) => r.average < 10).length;
    return { average, best: sorted[0], worst: sorted[sorted.length - 1], pass, fail };
  }, [results]);

  const rankedResults = useMemo(() => {
    const withAvg = results.filter((r) => r.average > 0);
    return [...withAvg]
      .sort((a, b) => b.average - a.average)
      .map((r, i) => ({ ...r, rank: i + 1 }));
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!searchTerm) return rankedResults;
    const t = searchTerm.toLowerCase();
    return rankedResults.filter((r) => (r.name || '').toLowerCase().includes(t));
  }, [rankedResults, searchTerm]);

  if (loading) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📊 Voir les résultats</h1>
        <p className="text-gray-500">Moyennes, classement et performances de vos classes</p>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">📚 Classe</label>
            <select
              value={selectedClass}
              onChange={handleClassChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
            >
              <option value="">-- Choisir une classe --</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.students_count} élèves)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">📖 Matière</label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
            >
              <option value="all">Toutes les matières</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">🔍 Rechercher</label>
            <input
              type="text"
              placeholder="Nom d'élève..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      {results.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
            <p className="text-xs text-gray-500 mb-1">📈 Moyenne classe</p>
            <p className={`text-2xl font-bold ${getAverageColor(parseFloat(stats.average))}`}>
              {stats.average}/20
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
            <p className="text-xs text-gray-500 mb-1">🏆 Meilleure</p>
            {stats.best ? (
              <>
                <p className="text-2xl font-bold text-green-600">{stats.best.average}/20</p>
                <p className="text-xs text-gray-500 mt-1 truncate">{stats.best.name}</p>
              </>
            ) : <p className="text-2xl font-bold text-gray-400">-</p>}
          </div>
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
            <p className="text-xs text-gray-500 mb-1">⚠️ Plus faible</p>
            {stats.worst ? (
              <>
                <p className="text-2xl font-bold text-orange-600">{stats.worst.average}/20</p>
                <p className="text-xs text-gray-500 mt-1 truncate">{stats.worst.name}</p>
              </>
            ) : <p className="text-2xl font-bold text-gray-400">-</p>}
          </div>
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
            <p className="text-xs text-gray-500 mb-1">✅ ≥ 10</p>
            <p className="text-2xl font-bold text-green-600">{stats.pass}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-red-500">
            <p className="text-xs text-gray-500 mb-1">❌ &lt; 10</p>
            <p className="text-2xl font-bold text-red-600">{stats.fail}</p>
          </div>
        </div>
      )}

      {/* Tableau */}
      {loadingResults ? (
        <div className="text-center py-8 text-gray-500">Chargement des résultats...</div>
      ) : results.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
          <p className="text-lg mb-2">📭 Aucun résultat disponible</p>
          <p className="text-sm">Saisissez des notes pour voir les résultats.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-2">
            <h3 className="font-bold text-[#2C3E50]">👨‍🎓 Résultats des élèves ({filteredResults.length})</h3>
            <p className="text-xs text-gray-500">Trié par moyenne décroissante</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Rang</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Élève</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Moyenne</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Appréciation</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        r.rank === 1 ? 'bg-yellow-400 text-white' :
                        r.rank === 2 ? 'bg-gray-300 text-white' :
                        r.rank === 3 ? 'bg-orange-400 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {r.rank}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium">{r.name}</td>
                    <td className={`py-3 px-4 font-bold ${getAverageColor(r.average)}`}>
                      {r.average}/20
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAppreciationColor(r.appreciation)}`}>
                        {r.appreciation}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {r.average >= 10 ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🟢 Admis</span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">🔴 En difficulté</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherResults;